import type { GroupSettings, GroupSettingsPatch } from './schema.js'
import { BLOCKLIST_ENTRY_MAX, DOMAIN_ENTRY_MAX, LIST_MAX, RULES_TEXT_MAX, TIMEZONE_MAX, USERID_ENTRY_MAX, WELCOME_TEXT_MAX } from './limits.js'

// The declarative description of the configurable surface. ONE table drives BOTH rendering
// (render.ts) and mutation (index.ts) — a rule beats N hand-written submenus (CLAUDE.md #3).
// `field` values are the EXACT GroupSettings sub-keys, so they double as the patch key and the
// read path; spec.test.ts guards them against the real Zod schema (typo + bounds drift).
//
// Lives in services/settings/ (NOT bot/features/config/) so BOTH frontends render from one table:
// the inline /config panel AND the v2 Mini App import it via the settings barrel. It is UI-agnostic
// — only a GroupSettings TYPE import, no grammY/Telegram (the web module may import it; it may NOT
// import the bot layer — docs/architecture.md §Boundary).
//
// A section is usually NESTED (its fields address settings[settingsKey][field]); a section with NO
// settingsKey is TOP-LEVEL — its fields address ROOT scalars (settings[field]). aiShield is the first
// top-level section (below), so a group can toggle it from the webapp like any other setting. Read/write a
// field via readFieldValue/buildFieldPatch (they resolve BOTH shapes) — never index settings directly.
//
// Still ABSENT (edited via bespoke controls, NOT this table). Their top-level keys live in the
// WEBAPP_BESPOKE_KEYS allowlist in spec.test.ts, which fails loud if a NEW scalar is ever added
// command-only (the "everything configurable from the webapp" invariant, mechanized — CLAUDE.md #7):
//   • logChannelId — a scalar channel ref; edited via /setlogchannel AND the Mini App's bespoke
//     self-verifying control (logchannel.ts + POST /api/log-channel — deliberately NOT a spec field).
//   • language — edited via the Mini App General-panel dropdown (main.ts) + the /config language row + /language.
//   • silent — a boolean edited via the Mini App General-panel toggle (main.ts) + the /config root row.

export type SectionKey = 'flood' | 'captcha' | 'warns' | 'locks' | 'raid' | 'filters' | 'cleanup' | 'trusted' | 'rules' | 'nightmode' | 'aishield'

interface FieldBase {
  /** Exact GroupSettings sub-key — used verbatim as the patch key and the read path. */
  field: string
  /** i18n key for the field's label. */
  labelKey: string
  /**
   * Optional cross-field dependency: this field only takes EFFECT while the named sibling (same section)
   * holds one of `in`; a UI greys it out + disables it otherwise. Mirrors the bot's behaviour — e.g. the
   * captcha timeoutSeconds/failAction govern ONLY the challenge, so they are inert when `type` is 'none'.
   * The sibling may also be a BOOL: the comparison stringifies the value, so gate on 'true'/'false'
   * (aiShieldBudget gates on aiShield this way — the section's master isn't named `enabled`, so the
   * master-toggle dimming rule doesn't cover it).
   */
  activeWhen?: { field: string, in: readonly string[] }
}
export interface BoolField extends FieldBase { kind: 'bool' }
export interface EnumField extends FieldBase { kind: 'enum', options: readonly string[] }
export interface IntField extends FieldBase {
  kind: 'int'
  /** Selectable values, strictly ascending, all within the schema's min/max (guarded by tests). */
  ladder: readonly number[]
  /** Display suffix appended by the renderer (e.g. "300s", "6h"). */
  unit?: 's' | 'h'
  /** i18n key shown INSTEAD of the number when value === 0 (e.g. "never"/"off"). */
  zeroLabelKey?: string
}
export interface TextField extends FieldBase {
  kind: 'text'
  /** The in-group command that also edits this field (the bot's /config shows it; the Mini App edits inline). */
  command: string
  /** true → the stored value is string[], edited one entry per line; absent → a scalar string (welcomeText). */
  list?: boolean
  /** Max length: per-entry for a list, total for a scalar. Mirrors the schema bound (limits.ts) — one source. */
  maxLen: number
  /** List only: max number of entries (mirrors the schema's array .max()). */
  maxItems?: number
  /**
   * List entry validation kind: 'domain' → lowercase + DOMAIN_RE (mirrors /allowdomain + detect.ts);
   * 'userid' → USERID_RE (digits only, NO lowercasing — mirrors /trust + the trusted enforcement lookup).
   */
  entry?: 'domain' | 'userid'
  /**
   * Optional hint rendered UNDER the field in the Mini App (e.g. the {name} placeholder for welcomeText).
   * The bot's /config doesn't render free-text inline (it points to `command`), so it ignores this.
   */
  hintKey?: string
  /**
   * Render the Mini App control from a KNOWN-VALUES source instead of the default multi-line <textarea>.
   * 'timezone' → a region-grouped dropdown over the IANA zones from Intl.supportedValuesOf('timeZone')
   * (browser-native, ~400 zones, nothing baked into the bundle) with a live zone clock beside it; an
   * engine without that API degrades to a free-text <input>. Still a normal command-backed text field:
   * the inline /config shows it READ-ONLY via `command` (zero inline changes), and the backend schema stays
   * the real gate — for 'timezone', nightModeWriteSchema's IANA refine rejects junk with a loud 400.
   * Absent ⇒ the usual <textarea>. Only meaningful on a SCALAR text field.
   */
  suggest?: 'timezone'
}
export type FieldSpec = BoolField | EnumField | IntField | TextField

export interface SectionSpec {
  key: SectionKey
  /**
   * The nested GroupSettings object this section edits (settings[settingsKey][field]). ABSENT ⇒ a
   * TOP-LEVEL section whose fields address ROOT scalars (settings[field]) — e.g. aiShield. Resolve values
   * via readFieldValue/buildFieldPatch (they handle both shapes); never index settings[settingsKey] directly.
   */
  settingsKey?: Extract<keyof GroupSettings, 'antiFlood' | 'welcomeCaptcha' | 'warns' | 'locks' | 'antiRaid' | 'filters' | 'cleanup' | 'trusted' | 'rules' | 'nightMode'>
  /** i18n key for the section title. */
  titleKey: string
  fields: readonly FieldSpec[]
}

// Section ORDER is presentational (both the Mini App form and the bot's inline /config render in this
// order). [v5, 2026-09-21] It is now ordered BY MEASURED USE, not by what we assumed mattered — user
// call, session 85, after the first real measurement of `audit_log` on the box. In the four live groups
// (~978 members) there are 120 real settings.update rows, 341 of 365 touching exactly ONE key each, so
// the counts are genuine single touches and not blanket saves:
//   nightMode 58 (48%!) · welcomeCaptcha 13 · antiFlood 9 · cleanup 8 · filters 8 · locks 6
//   antiRaid 4 · rules 4 · aiShield 3 · language 3 · silent 2
// The v4 order was close to the INVERSE of that: night mode — the single most-touched section — sat
// LAST of eleven, while AI Shield led as a hero panel although it is OFF in all four groups and ranks
// ninth. ⚠️ v4's lead was itself a user directive (session 62: "the differentiator other bots don't
// have"); session 85 REVERSES it on the evidence, with the user's explicit call. Don't silently
// restore the old order — it would be re-litigating a decision the measurement already settled.
// What the counts do NOT license is pruning: every one of the eleven sections has been touched at
// least once, so there is no dead section to cut. The problem was the order, not the size.
export const SECTIONS: readonly SectionSpec[] = [
  {
    key: 'captcha',
    settingsKey: 'welcomeCaptcha',
    titleKey: 'cfg-sec-captcha',
    fields: [
      { kind: 'bool', field: 'enabled', labelKey: 'cfg-f-enabled' },
      { kind: 'enum', field: 'type', labelKey: 'cfg-f-type', options: ['none', 'button', 'math'] },
      { kind: 'int', field: 'timeoutSeconds', labelKey: 'cfg-f-timeout', ladder: [30, 60, 120, 300, 600, 1800, 3600], unit: 's', activeWhen: { field: 'type', in: ['button', 'math'] } },
      { kind: 'enum', field: 'failAction', labelKey: 'cfg-f-failaction', options: ['kick', 'ban', 'mute'], activeWhen: { field: 'type', in: ['button', 'math'] } },
      { kind: 'int', field: 'deleteAfterSeconds', labelKey: 'cfg-f-deleteafter', ladder: [0, 30, 60, 300, 3600], unit: 's', zeroLabelKey: 'cfg-v-off' },
      { kind: 'bool', field: 'cleanPrevious', labelKey: 'cfg-f-cleanprevious' },
      { kind: 'text', field: 'welcomeText', labelKey: 'cfg-f-welcometext', command: '/setwelcome', maxLen: WELCOME_TEXT_MAX, hintKey: 'ui-name-hint' },
      { kind: 'text', field: 'goodbyeText', labelKey: 'cfg-f-goodbyetext', command: '/setgoodbye', maxLen: WELCOME_TEXT_MAX, hintKey: 'ui-name-hint' },
    ],
  },
  {
    key: 'nightmode',
    settingsKey: 'nightMode',
    titleKey: 'cfg-sec-nightmode',
    fields: [
      // The nightly read-only window (whole hours, in the group's timezone). startHour/endHour are clock
      // hours 0–23 (NOT durations → no unit suffix). No `activeWhen` on the hour/announce/timezone fields:
      // the renderer's master-`enabled` rule (form.ts controlInactive) already greys EVERY non-enabled field
      // while `enabled` is off — exactly like the other gated sections (flood, captcha, raid) — so the
      // schedule controls light up once night mode is turned on. (activeWhen is for enum-sibling gating, e.g.
      // captcha type → timeout; it isn't needed here.) The timezone is a scalar text field with `suggest`, so
      // the Mini App renders a searchable IANA picker (form.ts) while the inline /config still shows it
      // read-only via `command` — /setnighttz keeps working, and the write path is the schema's IANA refine.
      { kind: 'bool', field: 'enabled', labelKey: 'cfg-f-enabled' },
      { kind: 'int', field: 'startHour', labelKey: 'cfg-f-starthour', ladder: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23] },
      { kind: 'int', field: 'endHour', labelKey: 'cfg-f-endhour', ladder: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23] },
      { kind: 'bool', field: 'announce', labelKey: 'cfg-f-announce' },
      { kind: 'text', field: 'timezone', labelKey: 'cfg-f-timezone', command: '/setnighttz', maxLen: TIMEZONE_MAX, suggest: 'timezone' },
    ],
  },
  {
    key: 'flood',
    settingsKey: 'antiFlood',
    titleKey: 'cfg-sec-flood',
    fields: [
      { kind: 'bool', field: 'enabled', labelKey: 'cfg-f-enabled' },
      { kind: 'int', field: 'messages', labelKey: 'cfg-f-messages', ladder: [2, 4, 6, 8, 10, 15, 20, 30, 50, 100] },
      { kind: 'int', field: 'windowSeconds', labelKey: 'cfg-f-window', ladder: [1, 2, 3, 5, 10, 15, 30, 60, 120], unit: 's' },
      { kind: 'enum', field: 'action', labelKey: 'cfg-f-action', options: ['mute', 'delete', 'ban', 'warn'] },
    ],
  },
  {
    key: 'locks',
    settingsKey: 'locks',
    titleKey: 'cfg-sec-locks',
    fields: [
      { kind: 'bool', field: 'links', labelKey: 'cfg-f-links' },
      { kind: 'bool', field: 'forwards', labelKey: 'cfg-f-forwards' },
      { kind: 'bool', field: 'media', labelKey: 'cfg-f-media' },
      { kind: 'bool', field: 'stickers', labelKey: 'cfg-f-stickers' },
      { kind: 'bool', field: 'mentions', labelKey: 'cfg-f-mentions' },
      { kind: 'bool', field: 'channels', labelKey: 'cfg-f-channels' },
      { kind: 'bool', field: 'invites', labelKey: 'cfg-f-invites' },
    ],
  },
  {
    key: 'filters',
    settingsKey: 'filters',
    titleKey: 'cfg-sec-filters',
    fields: [
      { kind: 'enum', field: 'linkPolicy', labelKey: 'cfg-f-linkpolicy', options: ['allow', 'warn', 'delete'] },
      { kind: 'text', field: 'blocklist', labelKey: 'cfg-f-blocklist', command: '/addfilter', list: true, maxLen: BLOCKLIST_ENTRY_MAX, maxItems: LIST_MAX },
      { kind: 'text', field: 'allowlistDomains', labelKey: 'cfg-f-allowlist', command: '/allowdomain', list: true, maxLen: DOMAIN_ENTRY_MAX, maxItems: LIST_MAX, entry: 'domain' },
    ],
  },
  {
    key: 'cleanup',
    settingsKey: 'cleanup',
    titleKey: 'cfg-sec-cleanup',
    fields: [
      { kind: 'bool', field: 'joinLeave', labelKey: 'cfg-f-joinleave' },
      { kind: 'bool', field: 'pinned', labelKey: 'cfg-f-pinned' },
      { kind: 'bool', field: 'other', labelKey: 'cfg-f-other' },
    ],
  },
  {
    key: 'raid',
    settingsKey: 'antiRaid',
    titleKey: 'cfg-sec-raid',
    fields: [
      { kind: 'bool', field: 'enabled', labelKey: 'cfg-f-enabled' },
      { kind: 'int', field: 'joinThreshold', labelKey: 'cfg-f-jointhreshold', ladder: [2, 5, 10, 20, 50, 100, 500, 1000] },
      { kind: 'int', field: 'windowSeconds', labelKey: 'cfg-f-window', ladder: [1, 5, 10, 30, 60, 120, 300, 600], unit: 's' },
      { kind: 'int', field: 'lockdownSeconds', labelKey: 'cfg-f-lockdown', ladder: [0, 60, 300, 900, 3600, 86400], unit: 's', zeroLabelKey: 'cfg-v-off' },
    ],
  },
  {
    key: 'warns',
    settingsKey: 'warns',
    titleKey: 'cfg-sec-warns',
    fields: [
      { kind: 'int', field: 'limit', labelKey: 'cfg-f-limit', ladder: [1, 2, 3, 4, 5, 7, 10, 15, 20, 50] },
      { kind: 'enum', field: 'action', labelKey: 'cfg-f-action', options: ['mute', 'ban', 'kick'] },
      { kind: 'int', field: 'expiryHours', labelKey: 'cfg-f-expiry', ladder: [0, 1, 6, 12, 24, 72, 168, 720, 8760], unit: 'h', zeroLabelKey: 'cfg-v-never' },
    ],
  },
  {
    key: 'trusted',
    settingsKey: 'trusted',
    titleKey: 'cfg-sec-trusted',
    fields: [
      { kind: 'text', field: 'userIds', labelKey: 'cfg-f-trustedids', command: '/trust', list: true, maxLen: USERID_ENTRY_MAX, maxItems: LIST_MAX, entry: 'userid' },
    ],
  },
  {
    key: 'rules',
    settingsKey: 'rules',
    titleKey: 'cfg-sec-rules',
    fields: [
      // A single scalar text (no list/entry) — edited inline in the Mini App, or via /setrules in-group;
      // anyone reads it with /rules. Mirrors welcomeText's TextField shape.
      { kind: 'text', field: 'text', labelKey: 'cfg-f-rulestext', command: '/setrules', maxLen: RULES_TEXT_MAX },
    ],
  },
  {
    // AI Shield — a TOP-LEVEL section (NO settingsKey → its fields address root scalars: settings.aiShield,
    // settings.aiShieldBudget). Shadow mode: opt-in per group, default OFF; candidate messages are
    // AI-classified and "would-flag" verdicts are recorded as audit rows — NO action. The inline consent
    // caption it used to carry was REMOVED on the user's order (session 64; the data-flow notice still
    // lives in the /aishield command reply and the section's help sheet). The master toggle reuses the
    // cfg-f-enabled label ("Status") like the other master switches — the section head reads "AI Shield".
    // Placed LAST since session 85 (user call), where it led both frontends from session 62. Not a demotion
    // of the feature — a placement that matches the evidence: it is OFF in all four live groups and accounts
    // for 3 of 120 real config changes, so it was occupying the first screen of people who had come to
    // change something else. It keeps its accent panel and its activity readout, at the bottom.
    key: 'aishield',
    titleKey: 'cfg-sec-aishield',
    fields: [
      { kind: 'bool', field: 'aiShield', labelKey: 'cfg-f-enabled' },
      // The group's own daily cap on AI checks (cost bound; enforced by services/ai-shield/budget.ts via
      // the settings value). Ladder within the schema's 10–2000; default 200 sits on it. Gated on the
      // master toggle via activeWhen (stringified bool — see FieldBase.activeWhen), since this section's
      // master is named `aiShield`, not `enabled`.
      { kind: 'int', field: 'aiShieldBudget', labelKey: 'cfg-f-aibudget', ladder: [50, 100, 200, 500, 1000, 2000], activeWhen: { field: 'aiShield', in: ['true'] } },
    ],
  },
]

const SECTION_BY_KEY = new Map<string, SectionSpec>(SECTIONS.map(s => [s.key, s]))

/** Look up a section by its short key (the value carried in callback data). */
export function sectionByKey(key: string): SectionSpec | undefined {
  return SECTION_BY_KEY.get(key)
}

/** Look up a field within a section by its exact name. */
export function fieldByName(section: SectionSpec, field: string): FieldSpec | undefined {
  return section.fields.find(f => f.field === field)
}

/**
 * Read a field's current value, resolving BOTH section shapes: a nested section reads
 * settings[settingsKey][field]; a TOP-LEVEL section (no settingsKey) reads the root scalar settings[field].
 * PURE — the ONE read path shared by both frontends (Mini App form.ts, inline /config render.ts + index.ts),
 * so a top-level scalar can never be mis-read as settings[undefined][field]. Returns unknown; callers narrow
 * by the field's kind (the spec.test.ts typo guard proves `field` is a real key of the resolved scope).
 */
export function readFieldValue(settings: GroupSettings, section: SectionSpec, field: string): unknown {
  const scope = section.settingsKey === undefined ? settings : settings[section.settingsKey]
  return (scope as Record<string, unknown>)[field]
}

/**
 * Build the one-field PATCH both frontends send: `{ [settingsKey]: { [field]: value } }` for a nested
 * section, or `{ [field]: value }` for a TOP-LEVEL section (aiShield, …). The single builder that keeps the
 * shape correct for BOTH; the backend re-validates content against groupSettingsPatchSchema (service.ts), so
 * an off-domain value still can't slip through. Both branches are checked by the GroupSettingsPatch return
 * annotation (the literal is contextually typed against it), so neither needs a cast.
 */
export function buildFieldPatch(section: SectionSpec, field: string, value: boolean | string | number | string[]): GroupSettingsPatch {
  if (section.settingsKey === undefined)
    return { [field]: value }
  return { [section.settingsKey]: { [field]: value } }
}

/** Cycle an enum to the next option (wraps). An unknown `current` maps to the first option. */
export function nextEnum(options: readonly string[], current: string): string {
  const i = options.indexOf(current)
  // options is non-empty by construction; the ?? branch is unreachable in practice.
  return options[(i + 1) % options.length] ?? current
}

/**
 * Step an int ladder one notch up or down, CLAMPED at the ends (no wrap). An off-ladder `current`
 * moves to the nearest ladder value in the requested direction, or stays put if already past the end.
 */
export function stepLadder(ladder: readonly number[], current: number, dir: 'u' | 'd'): number {
  if (dir === 'u')
    return ladder.find(v => v > current) ?? current
  // down: the largest ladder entry strictly below `current` (ladder is ascending), else clamp.
  let best = current
  for (const v of ladder) {
    if (v < current)
      best = v
    else
      break
  }
  return best
}
